export const NOTABLE_LOADOUT_BATCH_SIZE = 8;

const normalizeText = (value) => String(value ?? '').trim();
const normalizeIdentity = (value) => normalizeText(value).toLowerCase();

export const normalizeNotableLoadoutManifest = (manifest) => {
  if (!manifest || typeof manifest !== 'object') {
    return null;
  }

  return {
    ...(manifest.templates !== undefined ? { templates: manifest.templates } : {}),
    notables: (Array.isArray(manifest?.notables) ? manifest.notables : [])
      .map((notable) => ({
        draftBeingId: normalizeText(notable?.draftBeingId),
        notableName: normalizeText(notable?.notableName),
        loadoutConcept: normalizeText(notable?.loadoutConcept),
        personalWealthProfile: normalizeText(notable?.personalWealthProfile),
        personalCashTargetGp: Number(notable?.personalCashTargetGp) || 0,
        itemSlots: (Array.isArray(notable?.itemSlots) ? notable.itemSlots : [])
          .map((slot) => ({
            notableItemSlotId: normalizeText(slot?.notableItemSlotId),
            ...(slot?.templateRef ? { templateRef: normalizeText(slot.templateRef) } : {}),
            itemPurpose: normalizeText(slot?.itemPurpose),
            itemConcept: normalizeText(slot?.itemConcept),
            powerTier: normalizeText(slot?.powerTier),
          }))
          .filter((slot) => slot.notableItemSlotId),
      }))
      .filter((notable) => notable.draftBeingId),
  };
};

export const getNotableLoadoutSlots = (manifest) => {
  const normalized = normalizeNotableLoadoutManifest(manifest);
  return (normalized?.notables ?? []).flatMap((notable) =>
    notable.itemSlots.map((slot) => ({
      ...slot,
      ownerDraftBeingId: notable.draftBeingId,
      ownerName: notable.notableName,
      loadoutConcept: notable.loadoutConcept,
    }))
  );
};

export const validateNotableLoadoutManifest = (manifest, residents) => {
  const normalized = normalizeNotableLoadoutManifest(manifest);
  const notables = (Array.isArray(residents) ? residents : [])
    .filter((resident) =>
      normalizeIdentity(resident?.populationTranche) === 'notables'
    );
  if (!normalized || normalized.notables.length !== notables.length) {
    throw new Error(
      `The notable loadout plan must include all ${notables.length} notables exactly once.`
    );
  }

  const residentsById = new Map(
    notables.map((resident) => [
      normalizeText(resident?.draftBeingId),
      normalizeText(resident?.name),
    ])
  );
  const seenResidentIds = new Set();
  const seenSlotIds = new Set();
  if (normalized.templates === undefined && getNotableLoadoutSlots(normalized).some(slot => slot.templateRef)) {
    throw new Error('Notable template references require an explicit shared template catalog.');
  }
  if (normalized.templates !== undefined) {
    if (!Array.isArray(normalized.templates) || normalized.templates.length > 1000 ||
      new Set(normalized.templates.map(template => template.templateRef)).size !== normalized.templates.length) {
      throw new Error('Notable item templates need distinct references.');
    }
    for (const slot of getNotableLoadoutSlots(normalized)) {
      const template = normalized.templates.find(entry => entry.templateRef === slot.templateRef);
      if (!template || template.itemConcept !== slot.itemConcept || template.powerTier !== slot.powerTier) {
        throw new Error('Notable slots must retain their exact shared template concept and power tier.');
      }
    }
  }
  normalized.notables.forEach((notable) => {
    if (
      !residentsById.has(notable.draftBeingId) ||
      residentsById.get(notable.draftBeingId) !== notable.notableName ||
      seenResidentIds.has(notable.draftBeingId)
    ) {
      throw new Error(
        `The notable loadout plan has an invalid resident assignment for ${notable.notableName || notable.draftBeingId}.`
      );
    }
    if (
      notable.itemSlots.length < 1 ||
      notable.itemSlots.length > 5 ||
      notable.personalCashTargetGp <= 0
    ) {
      throw new Error(
        `${notable.notableName} must have 1 to 5 Item slots and positive cash.`
      );
    }
    seenResidentIds.add(notable.draftBeingId);
    notable.itemSlots.forEach((slot) => {
      if (seenSlotIds.has(slot.notableItemSlotId)) {
        throw new Error(
          `Notable Item slot ${slot.notableItemSlotId} appears more than once.`
        );
      }
      seenSlotIds.add(slot.notableItemSlotId);
    });
  });
  return normalized;
};

export const getNotableLoadoutProgress = (manifest, itemDrafts) => {
  const slots = getNotableLoadoutSlots(manifest);
  const generatedSlotIds = new Set(
    (Array.isArray(itemDrafts) ? itemDrafts : [])
      .map((item) => normalizeText(item?.notableItemSlotId))
      .filter(Boolean)
  );
  const normalizedManifest = normalizeNotableLoadoutManifest(manifest);
  const notables = (normalizedManifest?.notables ?? []).map((notable) => {
    const generatedCount = notable.itemSlots
      .filter((slot) => generatedSlotIds.has(slot.notableItemSlotId))
      .length;
    return {
      ...notable,
      generatedCount,
      targetCount: notable.itemSlots.length,
      complete: generatedCount >= notable.itemSlots.length,
    };
  });

  return {
    notables,
    generatedCount: slots.filter((slot) =>
      generatedSlotIds.has(slot.notableItemSlotId)
    ).length,
    targetCount: slots.length,
    complete: slots.length > 0 && slots.every((slot) =>
      generatedSlotIds.has(slot.notableItemSlotId)
    ),
  };
};

export const getNextNotableLoadoutBatch = (
  manifest,
  itemDrafts,
  batchSize = NOTABLE_LOADOUT_BATCH_SIZE
) => {
  const completedSlotIds = new Set(
    (Array.isArray(itemDrafts) ? itemDrafts : [])
      .map((item) => normalizeText(item?.notableItemSlotId))
      .filter(Boolean)
  );
  const requestedSlots = getNotableLoadoutSlots(manifest)
    .filter((slot) => !completedSlotIds.has(slot.notableItemSlotId))
    .slice(0, Math.max(1, Math.floor(Number(batchSize) || 1)));

  return requestedSlots.length > 0 ? requestedSlots : null;
};

export const mergeNotableLoadoutItemBatch = (
  currentItemDrafts,
  returnedItemDrafts,
  requestedSlots
) => {
  const current = Array.isArray(currentItemDrafts) ? currentItemDrafts : [];
  const returned = Array.isArray(returnedItemDrafts) ? returnedItemDrafts : [];
  const requested = Array.isArray(requestedSlots) ? requestedSlots : [];
  const expectedSlotIds = new Set(
    requested.map((slot) => normalizeText(slot?.notableItemSlotId))
  );
  const returnedSlotIds = new Set(
    returned.map((item) => normalizeText(item?.notableItemSlotId))
  );
  if (
    returned.length !== requested.length ||
    returnedSlotIds.size !== expectedSlotIds.size ||
    [...expectedSlotIds].some((slotId) => !returnedSlotIds.has(slotId))
  ) {
    throw new Error(
      'Notable Item generation did not return every assigned slot.'
    );
  }

  const completedSlotIds = new Set(
    current.map((item) => normalizeText(item?.notableItemSlotId))
  );
  if (returned.some((item) =>
    completedSlotIds.has(normalizeText(item?.notableItemSlotId))
  )) {
    throw new Error('Notable Item generation reopened a completed slot.');
  }
  return [...current, ...returned];
};

export const generateNotableLoadoutBatchesSerially = async ({
  manifest,
  itemDrafts = [],
  batchSize = NOTABLE_LOADOUT_BATCH_SIZE,
  requestBatch,
  onBatch,
  shouldContinue,
} = {}) => {
  if (typeof requestBatch !== 'function') {
    throw new Error('A notable Item batch request function is required.');
  }

  let currentItemDrafts = Array.isArray(itemDrafts) ? itemDrafts : [];
  let batchCount = 0;
  let stopped = false;
  while (true) {
    if (typeof shouldContinue === 'function' && !shouldContinue()) {
      stopped = true;
      break;
    }
    const requestedSlots = getNextNotableLoadoutBatch(
      manifest,
      currentItemDrafts,
      batchSize
    );
    if (!requestedSlots) {
      break;
    }
    const returnedItemDrafts = await requestBatch({
      requestedSlots,
      currentItemDrafts,
      batchNumber: batchCount + 1,
    });
    currentItemDrafts = mergeNotableLoadoutItemBatch(
      currentItemDrafts,
      returnedItemDrafts,
      requestedSlots
    );
    batchCount += 1;
    if (typeof onBatch === 'function') {
      await onBatch({
        requestedSlots,
        returnedItemDrafts,
        itemDrafts: currentItemDrafts,
        batchNumber: batchCount,
      });
    }
  }

  return {
    itemDrafts: currentItemDrafts,
    batchCount,
    stopped,
  };
};

export const attachNotableCurrencyHoldings = (residents, holdings) => {
  const holdingsByResidentId = new Map(
    (Array.isArray(holdings) ? holdings : [])
      .map((holding) => [
        normalizeText(holding?.draftBeingId),
        holding,
      ])
      .filter(([draftBeingId]) => draftBeingId)
  );

  return (Array.isArray(residents) ? residents : []).map((resident) => {
    const holding = holdingsByResidentId.get(
      normalizeText(resident?.draftBeingId)
    );
    return holding
      ? {
          ...resident,
          currencies: holding.currencies,
          currencyTargetGp: holding.targetCashGp,
          currencyActualGp: holding.actualCashGp,
        }
      : resident;
  });
};
